import {
    Book20Regular,
    Person20Regular,
    ChartMultiple20Regular,
    Lightbulb20Regular,
    DocumentBulletList20Regular,
    Bot20Regular,
    ChatBubblesQuestion20Regular,
    ChevronRight20Regular,
    Dismiss24Regular,
    Warning20Regular
} from "@fluentui/react-icons";
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { logEvent, logPageView, startPageLoadTimeTracker, stopPageLoadTimeTracker } from "../helper/AppInsightsHelper";
import { useGetMCSResourcesQuery, useLazyGetCopilotAgentDirectLineTokenQuery, useGetTrainingCoursesQuery } from "../slices/ExploreProducts.slice";
import { ITrainingCourse, formatDuration, formatDate, getTimeAgo } from "../interfaces/TrainingCourse.interfaces";
import CopilotLogo from "../assets/Microsoft-Copilot-Logo.png";

// Declare WebChat global
declare global {
    interface Window {
        WebChat: any;
    }
}

// MCS Resource interface from API
interface IMCSResource {
    id: number;
    resourceId: string;
    category: string;
    title: string;
    description: string | null;
    link: string | null;
    subCategory: string | null;
    iconName: string | null;
    sortOrder: number;
    isActive: boolean;
}

// MCS Tab definitions - Security 360 blue theme
const MCS_TABS = [
    { id: "playbooks", label: "Playbooks", icon: Book20Regular, color: "#0078D4" },
    { id: "knowme", label: "Know Me", icon: Person20Regular, color: "#2B88D8" },
    { id: "reports", label: "Reports", icon: ChartMultiple20Regular, color: "#106EBE" },
    { id: "training", label: "Training", icon: Lightbulb20Regular, color: "#005A9E" },
    { id: "planning", label: "Planning", icon: DocumentBulletList20Regular, color: "#004E8C" },
    { id: "ai", label: "AI", icon: Bot20Regular, color: "#004578" },
    { id: "feedback", label: "Feedback", icon: ChatBubblesQuestion20Regular, color: "#0063B1" },
];

// Playbook sub-filters
const PLAYBOOK_FILTERS = [
    { id: "all", label: "All" },
    { id: "customer-onboarding", label: "Customer Onboarding" },
    { id: "issue-resolution", label: "Issue Resolution" },
    { id: "service-improvement", label: "Service Improvement Plan" },
    { id: "roadmap-insights", label: "Roadmap Insights" },
    { id: "design-change-request", label: "Design Change Request" },
    { id: "cle-wiki", label: "CLE Wiki" },
    { id: "css-wiki", label: "CSS Wiki" }
];

// Props interface
interface MCSReportCatalogProps {
    apiData?: any[];
    isLoading?: boolean;
}

// MCS Resource Card Component
interface MCSResourceCardProps {
    title: string;
    description: string;
    hasLink?: boolean;
    color: string;
    onClick?: () => void;
    index: number;
    icon?: React.ElementType;
}

const MCSResourceCard: React.FC<MCSResourceCardProps> = ({ title, description, hasLink = true, color, onClick, index, icon: IconComponent }) => {
    const lightColor = `${color}15`;
    const mediumColor = `${color}30`;

    return (
        <div
            className="mcs-card"
            style={{
                position: "relative",
                background: `linear-gradient(135deg, #FFFFFF 0%, ${lightColor} 100%)`,
                border: "1px solid #E1E5EB",
                borderLeft: `4px solid ${color}`,
                borderRadius: "16px",
                padding: "0",
                overflow: "hidden",
                cursor: hasLink ? "pointer" : "default",
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 8px rgba(0, 120, 212, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
                animation: `fadeInUp 0.3s ease ${index * 0.05}s both`
            }}
            onClick={hasLink ? onClick : undefined}
        >
            <div style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "120px",
                height: "120px",
                background: `radial-gradient(circle at top right, ${mediumColor} 0%, transparent 70%)`,
                pointerEvents: "none"
            }} />

            <div style={{
                padding: "20px 24px",
                display: "flex",
                alignItems: "flex-start",
                gap: "14px",
                position: "relative",
                zIndex: 1
            }}>
                {IconComponent && (
                    <div style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: `0 4px 12px ${color}40`
                    }}>
                        <IconComponent style={{ fontSize: "20px", color: "#FFFFFF" }} />
                    </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#1A1A2E",
                        marginBottom: "6px",
                        letterSpacing: "-0.2px",
                        lineHeight: 1.3
                    }}>{title}</div>
                    <div style={{
                        fontSize: "13px",
                        color: "#5A5A72",
                        lineHeight: 1.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                    }}>{description}</div>
                </div>

                {hasLink && (
                    <span
                        className="mcs-card-arrow"
                        style={{
                            marginLeft: "8px",
                            width: "32px",
                            height: "32px",
                            borderRadius: "10px",
                            background: `linear-gradient(135deg, #F3F6FC 0%, ${lightColor} 100%)`,
                            border: `1px solid ${mediumColor}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: color,
                            fontSize: "16px",
                            transition: "all 0.2s ease",
                            flexShrink: 0
                        }}>
                        <ChevronRight20Regular />
                    </span>
                )}
            </div>
        </div>
    );
};

// Main MCS Report Catalog Component
const MCSReportCatalog: React.FC<MCSReportCatalogProps> = ({ apiData = [], isLoading = false }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("playbooks");
    const [activeFilter, setActiveFilter] = useState("all");
    const [expandedTrainingId, setExpandedTrainingId] = useState<number | null>(1); // First course expanded by default

    // Fetch MCS resources from API
    const { data: mcsResources = [], isLoading: isMcsLoading } = useGetMCSResourcesQuery(null);

    // Fetch Training courses from API
    const { data: apiTrainingCourses = [], isLoading: isTrainingLoading } = useGetTrainingCoursesQuery(null);

    // Training courses from API
    const trainingCourses = useMemo(() => {
        return (apiTrainingCourses as ITrainingCourse[]).filter(c => c.isActive);
    }, [apiTrainingCourses]);

    // Training stats for banner
    const trainingStats = useMemo(() => {
        const totalCourses = trainingCourses.length;
        const totalMinutes = trainingCourses.reduce((sum, c) => sum + c.durationMinutes, 0);
        return { totalCourses, totalMinutes };
    }, [trainingCourses]);

    // Agent Panel state
    const [isAgentPanelOpen, setIsAgentPanelOpen] = useState(false);
    const [agentLoading, setAgentLoading] = useState(false);
    const [agentConnected, setAgentConnected] = useState(false);
    const [agentError, setAgentError] = useState<string | null>(null);
    const [isWaitingResponse, setIsWaitingResponse] = useState(false);
    const [directLineToken, setDirectLineToken] = useState<string | null>(null);
    const [isFetchingToken, setIsFetchingToken] = useState(false);
    const webChatRef = useRef<HTMLDivElement>(null);
    const webChatInitialized = useRef(false);

    // API hook for getting DirectLine token
    const [getDirectLineToken] = useLazyGetCopilotAgentDirectLineTokenQuery();

    // MSAL hooks
    const { accounts } = useMsal();
    const currentUser = accounts[0];
    const userName = currentUser?.name || "User";
    const userEmail = currentUser?.username || "";

    useEffect(() => {
        // Track page view and session duration
        logPageView("MCS Report Catalog", window.location.href, { Category: "MCS" });
        startPageLoadTimeTracker("MCS Report Catalog");

        // Track session end when user leaves page
        return () => {
            stopPageLoadTimeTracker("MCS Report Catalog", window.location.href);
            logEvent("MCS_Session_End", "MCS Report Catalog", "SessionTracking", {
                activeTab: activeTab
            });
        };
    }, []);

    // Load WebChat SDK dynamically
    const loadWebChat = useCallback(async () => {
        if (window.WebChat) return;

        await new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdn.botframework.com/botframework-webchat/latest/webchat.js";
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Failed to load WebChat"));
            document.head.appendChild(script);
        });
    }, []);

    // Initialize WebChat with SSO - exact CustomAgent logic
    const initializeWebChat = useCallback(async () => {
        if (webChatInitialized.current) return;
        if (!directLineToken) return; // Token must be fetched first

        try {
            setAgentLoading(true);
            setAgentError(null);

            await loadWebChat();

            // Use the already-fetched DirectLine token
            const directLine = window.WebChat.createDirectLine({ token: directLineToken });

            // Custom middleware to process Copilot Studio citations and append links to messages
            const processCitations = (activity: any) => {
                if (!activity || activity.type !== "message" || activity.from?.role === "user") {
                    return activity;
                }

                // Check for citations in various locations Copilot Studio may send them
                const citations: Array<{ title: string; url: string }> = [];

                // Check activity.entities for citation data
                if (activity.entities && Array.isArray(activity.entities)) {
                    activity.entities.forEach((entity: any) => {
                        if (entity.type === "https://schema.org/Message" && entity.citation) {
                            const cite = entity.citation;
                            if (cite.url && cite.title) {
                                citations.push({ title: cite.title, url: cite.url });
                            }
                        }
                        // Alternative citation format
                        if (entity["@type"] === "Claim" && entity.appearance?.url) {
                            citations.push({
                                title: entity.appearance?.name || "Source",
                                url: entity.appearance.url
                            });
                        }
                    });
                }

                // Check channelData for Copilot-specific citations
                if (activity.channelData?.citations && Array.isArray(activity.channelData.citations)) {
                    activity.channelData.citations.forEach((cite: any) => {
                        if (cite.url && cite.title) {
                            citations.push({ title: cite.title, url: cite.url });
                        }
                    });
                }

                // Check for citation references in the text like [1], [2], etc. and match with entities
                if (activity.channelData?.["bing-citations"] && Array.isArray(activity.channelData["bing-citations"])) {
                    activity.channelData["bing-citations"].forEach((cite: any) => {
                        if (cite.url && cite.name) {
                            citations.push({ title: cite.name, url: cite.url });
                        }
                    });
                }

                // Append citations as links to the message if found
                if (citations.length > 0) {
                    const uniqueCitations = citations.filter((cite, index, self) =>
                        index === self.findIndex((c) => c.url === cite.url)
                    );

                    const citationLinks = uniqueCitations
                        .map((cite) => `[${cite.title}](${cite.url})`)
                        .join(" | ");

                    activity.text = activity.text
                        ? `${activity.text}\n\n**Sources:** ${citationLinks}`
                        : `**Sources:** ${citationLinks}`;
                }

                return activity;
            };

            const store = window.WebChat.createStore(
                {},
                () => (next: any) => (action: any) => {
                    // Track when user sends a message - show loading
                    if (action.type === "DIRECT_LINE/POST_ACTIVITY" ||
                        action.type === "WEB_CHAT/SEND_MESSAGE") {
                        const activity = action.payload?.activity;
                        if (!activity || activity?.type === "message") {
                            setIsWaitingResponse(true);
                            // Track agent message sent
                            logEvent("MCS_Agent_MessageSent", "MCS Report Catalog", "AgentInteraction", {
                                messageType: activity?.type || "message"
                            });
                        }
                    }

                    if (action.type === "DIRECT_LINE/INCOMING_ACTIVITY") {
                        const activity = action.payload?.activity;

                        // Process citations from Copilot Studio generative answers
                        if (activity?.type === "message" && activity?.from?.role !== "user") {
                            action.payload.activity = processCitations(activity);
                            setIsWaitingResponse(false);
                            // Track agent response received
                            logEvent("MCS_Agent_ResponseReceived", "MCS Report Catalog", "AgentInteraction");
                        }

                        // Note: OAuth card will render as a Sign In button for users to click
                        // SSO token exchange was removed due to federated credentials limitation
                    }
                    return next(action);
                }
            );

            const styleOptions = {
                // Background - consistent white
                backgroundColor: "#ffffff",

                // Bot messages
                bubbleBackground: "#f3f4f6",
                bubbleBorderColor: "#e5e7eb",
                bubbleBorderRadius: 12,
                bubbleBorderWidth: 1,
                bubbleTextColor: "#1f2937",

                // Link styling - ensure markdown links are visible and clickable
                markdownRespectCRLF: true,
                messageActivityWordBreak: "break-word",
                accent: "#0078d4", // Link color

                // User messages
                bubbleFromUserBackground: "#0078d4",
                bubbleFromUserBorderRadius: 12,
                bubbleFromUserTextColor: "#ffffff",

                // Send box
                sendBoxBackground: "#ffffff",
                sendBoxBorderTop: "1px solid #e5e7eb",
                sendBoxButtonColor: "#0078d4",
                sendBoxButtonColorOnHover: "#005a9e",
                sendBoxPlaceholderColor: "#9ca3af",
                sendBoxTextColor: "#1f2937",
                sendBoxHeight: 50,

                // Avatars
                botAvatarInitials: "AI",
                botAvatarBackgroundColor: "#0078d4",
                userAvatarInitials: userName.substring(0, 2).toUpperCase(),
                userAvatarBackgroundColor: "#6366f1",
                avatarSize: 32,

                // Typing indicator - enhanced
                typingAnimationDuration: 400,
                typingAnimationHeight: 24,
                showTypingIndicator: true,

                // Other
                hideUploadButton: true,
                suggestedActionLayout: "stacked",
                suggestedActionBackground: "#ffffff",
                suggestedActionBorderColor: "#e5e7eb",
                suggestedActionBorderRadius: 8,
                suggestedActionTextColor: "#0078d4",

                // Root dimensions
                rootHeight: "100%",
                rootWidth: "100%"
            };

            if (webChatRef.current) {
                window.WebChat.renderWebChat(
                    {
                        directLine,
                        store,
                        styleOptions,
                        userID: userEmail,
                        username: userName
                    },
                    webChatRef.current
                );
            }

            webChatInitialized.current = true;
            setAgentConnected(true);
            setAgentLoading(false);
            logEvent("MCS Agent connected", "MCS Report Catalog", "AgentConnect");
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to initialize chat";
            setAgentError(errorMessage);
            setAgentLoading(false);
        }
    }, [loadWebChat, directLineToken, userName, userEmail]);

    // Handle opening the agent panel - fetch token first, then open
    const handleOpenAgentPanel = useCallback(async () => {
        if (directLineToken) {
            // Token already fetched, just open the panel
            setIsAgentPanelOpen(true);
            logEvent("MCS Agent opened", "MCS Report Catalog", "AgentOpen");
            return;
        }

        try {
            setIsFetchingToken(true);
            const tokenResult = await getDirectLineToken();
            if (!tokenResult.data?.token) {
                throw new Error("Failed to get DirectLine token from API");
            }
            setDirectLineToken(tokenResult.data.token);
            setIsAgentPanelOpen(true);
            logEvent("MCS Agent opened", "MCS Report Catalog", "AgentOpen");
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to connect to agent";
            setAgentError(errorMessage);
        } finally {
            setIsFetchingToken(false);
        }
    }, [directLineToken, getDirectLineToken]);

    // Initialize WebChat when panel opens and token is available
    useEffect(() => {
        if (isAgentPanelOpen && directLineToken && !webChatInitialized.current) {
            // Token is ready - initialize WebChat
            const timer = setTimeout(() => {
                initializeWebChat();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isAgentPanelOpen, directLineToken, initializeWebChat]);

    // Helper function to get resources by category from API data
    const getResourcesByCategory = useCallback((category: string) => {
        return (mcsResources as IMCSResource[])
            .filter((r: IMCSResource) => r.category === category)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((r: IMCSResource) => ({
                id: r.resourceId,
                title: r.title,
                description: r.description || "",
                link: r.link || "",
                subCategory: r.subCategory
            }));
    }, [mcsResources]);

    // Filter playbooks by sub-category
    const filteredPlaybooks = useMemo(() => {
        const playbooks = getResourcesByCategory("playbooks");
        if (activeFilter === "all") return playbooks;
        return playbooks.filter((pb: any) => pb.subCategory === activeFilter);
    }, [activeFilter, getResourcesByCategory]);

    // Get tab data from API
    const getMCSTabData = useCallback(() => {
        switch (activeTab) {
            case "playbooks":
                return filteredPlaybooks;
            case "knowme":
                return getResourcesByCategory("knowme");
            case "reports": {
                // Combine API MCS resources with existing apiData for backwards compatibility
                const dbReports = getResourcesByCategory("reports");
                const mcsApiReports = apiData?.filter((r: any) => r.scenarios === "MCS") || [];
                const additionalReports = mcsApiReports.map((r: any) => ({
                    id: r.reportName,
                    title: r.reportName,
                    description: r.oneliner || "View report",
                    link: r.reportLink
                }));
                // Merge without duplicates
                const allReports = [...dbReports];
                additionalReports.forEach((r: any) => {
                    if (!allReports.some((existing) => existing.title === r.title)) {
                        allReports.push(r);
                    }
                });
                return allReports;
            }
            case "training":
                return getResourcesByCategory("training");
            case "planning":
                return getResourcesByCategory("planning");
            case "ai":
                return getResourcesByCategory("ai");
            case "feedback":
                return getResourcesByCategory("feedback");
            case "dcr":
                return getResourcesByCategory("dcr");
            default:
                return [];
        }
    }, [activeTab, filteredPlaybooks, apiData, getResourcesByCategory]);

    // Get tab info
    const getTabInfo = useCallback(() => {
        switch (activeTab) {
            case "playbooks":
                return { title: "Playbooks", description: "Consolidated playbooks, guides, checklists, process flows, and reference materials - sub-divided by feature" };
            case "knowme":
                return { title: "Know Me", description: "Power BI report entry point for Know Me insights" };
            case "reports":
                return { title: "Reports", description: "Links to Power BI dashboards including the MCS PBI app and Support Health 360" };
            case "training":
                return { title: "Training", description: "Training resources and materials for MCS team members" };
            case "planning":
                return { title: "Planning", description: "Planning resources and templates for MCS engagements" };
            case "ai":
                return { title: "AI", description: "AI-powered tools and assistants for MCS workflows" };
            case "feedback":
                return { title: "Feedback", description: "Share feedback and feature requests for MCS tools" };
            case "dcr":
                return { title: "DCR", description: "Design Change Requests management and UPI scenarios" };
            default:
                return { title: "", description: "" };
        }
    }, [activeTab]);

    // Helper function to check if link is an embeddable Power BI report (not an App)
    // Power BI Apps have X-Frame-Options that block iframe embedding
    const isEmbeddablePowerBIReport = useCallback((link: string): boolean => {
        if (!link) return false;
        // Power BI Apps (/apps/) cannot be embedded - X-Frame-Options blocks it
        if (link.includes("/apps/")) return false;
        // Only embed direct report links
        return (link.includes("reportEmbed") || link.includes("/reports/")) &&
            (link.includes("msit.powerbi.com") || link.includes("app.powerbi.com"));
    }, []);

    // Helper function to extract reportId from Power BI link (for direct reports only)
    const extractPowerBIReportId = useCallback((link: string): string | null => {
        if (!link) return null;
        // Power BI Apps cannot be embedded - skip them
        if (link.includes("/apps/")) return null;
        // Match reportId parameter from various Power BI URL formats
        const reportIdMatch = link.match(/reportId[=:]([a-f0-9-]+)/i);
        if (reportIdMatch) return reportIdMatch[1];
        // Try alternative format: /reports/{reportId}
        const reportsMatch = link.match(/\/reports\/([a-f0-9-]+)/i);
        if (reportsMatch) return reportsMatch[1];
        return null;
    }, []);

    // Handle card click
    const handleCardClick = useCallback((item: any) => {
        logEvent(`Card clicked: ${item.title}`, "MCS Report Catalog", activeTab);
        if (item.link) {
            // Check if it's an embeddable Power BI report (not an App)
            if (isEmbeddablePowerBIReport(item.link)) {
                const reportId = extractPowerBIReportId(item.link);
                if (reportId) {
                    navigate(`/supportreportview/reportid=${reportId}`, {
                        state: {
                            reportName: item.title,
                            reportLink: item.link,
                            category: "MCS"
                        }
                    });
                    return;
                }
            }
            // For Power BI Apps and non-Power BI links, open in new tab
            window.open(item.link, "_blank", "noopener,noreferrer");
        }
    }, [activeTab, isEmbeddablePowerBIReport, extractPowerBIReportId, navigate]);

    const tabInfo = getTabInfo();
    const tabData = getMCSTabData();

    return (
        <div style={{ minHeight: "100vh", background: "#FFFFFF", fontFamily: "'Roboto', sans-serif" }}>
            {/* Global Animations */}
            <style>
                {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: none; }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          .mcs-card {
            position: relative;
          }
          .mcs-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%);
            background-size: 200% 100%;
            opacity: 0;
            transition: opacity 0.3s ease;
            pointer-events: none;
            border-radius: 16px;
            z-index: 2;
          }
          .mcs-card:hover::before {
            opacity: 1;
            animation: shimmer 1.5s ease-in-out;
          }
          .mcs-card:hover {
            border-color: #0078D4;
            box-shadow: 0 12px 32px rgba(0, 120, 212, 0.18), 0 4px 12px rgba(0, 0, 0, 0.06);
            transform: translateY(-4px) scale(1.01);
          }
          .mcs-card:hover .mcs-card-arrow {
            background: linear-gradient(135deg, #0078D4 0%, #2B88D8 100%);
            border-color: transparent;
            color: #FFFFFF;
            transform: translateX(2px);
          }
          .mcs-tab-pill:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0, 120, 212, 0.15);
          }
          .mcs-filter-pill:hover {
            border-color: #0078D4;
            color: #0078D4;
          }
        `}
            </style>

            {/* Header Section */}
            <div style={{
                background: "#FFFFFF",
                padding: "10px 40px 32px",
                borderBottom: "1px solid #E1E5EB",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif"
            }}>
                {/* Breadcrumb */}
                <div style={{ marginBottom: "35px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#5A5A72", fontSize: "13px" }}>
                        <span style={{ cursor: "pointer", color: "#0078D4" }} onClick={() => navigate("/")}>Home</span>
                        <ChevronRight20Regular style={{ fontSize: "12px", color: "#8E8EA0" }} />
                        <span style={{ cursor: "pointer", color: "#0078D4" }} onClick={() => navigate("/explorereports?tab=supportCatalog")}>Explore Reports</span>
                        <ChevronRight20Regular style={{ fontSize: "12px", color: "#8E8EA0" }} />
                        <span style={{ fontWeight: 500, color: "#1A1A2E" }}>MCS</span>
                    </div>
                </div>

                {/* Title */}
                <div style={{ textAlign: "center" }}>
                    <h1 style={{
                        fontSize: "26px",
                        fontWeight: 300,
                        color: "#1A1A2E",
                        marginBottom: "6px",
                        letterSpacing: "-0.3px"
                    }}>
                        Welcome to <span style={{ fontWeight: 600, color: "#0078D4" }}>Mission Critical Services</span> Unified Portal
                    </h1>
                    <p style={{
                        fontSize: "14px",
                        color: "#5A5A72",
                        margin: 0
                    }}>
                        Your single destination for all Mission Critical Services resources
                    </p>
                </div>
            </div>

            {/* Category Tab Pills */}
            <div style={{
                padding: "24px 40px 0",
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "10px",
                background: "#FFFFFF",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif"
            }}>
                {MCS_TABS.map((tab) => {
                    const IconComponent = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <div
                            key={tab.id}
                            className="mcs-tab-pill"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "7px",
                                padding: "10px 22px",
                                borderRadius: "28px",
                                border: `1.5px solid ${isActive ? "transparent" : "#E1E5EB"}`,
                                background: isActive ? tab.color : "#FFFFFF",
                                fontSize: "13px",
                                fontWeight: 500,
                                color: isActive ? "#fff" : "#5A5A72",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: isActive ? "0 4px 16px rgba(0, 120, 212, 0.25)" : "0 1px 3px rgba(0, 0, 0, 0.04)"
                            }}
                            onClick={() => {
                                setActiveTab(tab.id);
                                setActiveFilter("all");
                                logEvent(`Tab changed to ${tab.id}`, "MCS Report Catalog", "TabChange");
                            }}
                        >
                            <IconComponent style={{ fontSize: "15px", color: isActive ? "#fff" : tab.color }} />
                            {tab.label}
                        </div>
                    );
                })}
            </div>

            {/* Main Content Area */}
            <div style={{
                maxWidth: "1120px",
                margin: "0 auto",
                padding: "36px 40px 60px",
                background: "#FFFFFF",
                minHeight: "calc(100vh - 350px)",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif"
            }}>
                {/* Pane Header - Hidden for Training tab which has its own header */}
                {activeTab !== "training" && (
                    <div style={{
                        marginBottom: "24px",
                        animation: "fadeInUp 0.4s ease"
                    }}>
                        <h2 style={{
                            fontSize: "18px",
                            fontWeight: 700,
                            color: "#0078D4",
                            marginBottom: "4px",
                            letterSpacing: "-0.3px"
                        }}>{tabInfo.title}</h2>
                        <p style={{
                            fontSize: "13px",
                            color: "#5A5A72",
                            margin: 0,
                            fontWeight: 400
                        }}>{tabInfo.description}</p>
                    </div>
                )}

                {/* Sub-filters for Playbooks */}
                {activeTab === "playbooks" && (
                    <div style={{
                        display: "flex",
                        gap: "8px",
                        marginBottom: "20px",
                        flexWrap: "wrap",
                        animation: "fadeInUp 0.5s ease"
                    }}>
                        {PLAYBOOK_FILTERS.map((filter) => (
                            <div
                                key={filter.id}
                                className="mcs-filter-pill"
                                style={{
                                    padding: "7px 18px",
                                    borderRadius: "20px",
                                    border: `1px solid ${activeFilter === filter.id ? "#0078D4" : "#E1E5EB"}`,
                                    background: activeFilter === filter.id ? "#0078D4" : "#FFFFFF",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: activeFilter === filter.id ? "#fff" : "#5A5A72",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease"
                                }}
                                onClick={() => {
                                    setActiveFilter(filter.id);
                                    logEvent("MCS_Filter_Changed", "MCS Report Catalog", "FilterChange", {
                                        filter: filter.id,
                                        filterLabel: filter.label
                                    });
                                }}
                            >
                                {filter.label}
                            </div>
                        ))}
                    </div>
                )}

                {/* Loading State */}
                {(isLoading || isMcsLoading) ? (
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px"
                    }}>
                        <div style={{
                            width: "40px",
                            height: "40px",
                            border: "3px solid #E1E5EB",
                            borderTopColor: "#0078D4",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite"
                        }} />
                        <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
                    </div>
                ) : (
                    /* Resource Cards Grid */
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "14px",
                        animation: "fadeInUp 0.4s ease"
                    }}>
                        {tabData.length > 0 ? (
                            tabData.map((item: any, index: number) => {
                                const currentTab = MCS_TABS.find(t => t.id === activeTab);
                                return (
                                    <MCSResourceCard
                                        key={item.id || index}
                                        title={item.title}
                                        description={item.description}
                                        hasLink={!!item.link}
                                        color={currentTab?.color || "#0078D4"}
                                        icon={currentTab?.icon}
                                        onClick={() => handleCardClick(item)}
                                        index={index}
                                    />
                                );
                            })
                        ) : (
                            <div style={{
                                gridColumn: "1 / -1",
                                textAlign: "center",
                                padding: "60px 24px",
                                color: "#5A5A72",
                                background: "#FFFFFF",
                                borderRadius: "14px",
                                border: "1px dashed #E1E5EB"
                            }}>
                                <ChartMultiple20Regular style={{ fontSize: "48px", color: "#B0B0C0", marginBottom: "16px" }} />
                                <p style={{ margin: 0, fontSize: "14px" }}>No resources available for this category.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Featured Training Banner - Shows inside Training tab below the cards */}
                {activeTab === "training" && !isTrainingLoading && trainingCourses.length > 0 && (
                    <div style={{
                        marginTop: "32px",
                        padding: "24px",
                        background: "#F9FAFB",
                        borderRadius: "12px",
                        border: "1px solid #E1E5EB"
                    }}>
                        {/* Featured Banner Header */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "20px"
                        }}>
                            <h2 style={{
                                fontSize: "22px",
                                fontWeight: 600,
                                color: "#1A1A2E",
                                margin: 0
                            }}>
                                Featured this month
                            </h2>
                            {(() => {
                                const requiredCourses = trainingCourses.filter(c => c.isActive && c.isRequired);
                                const requiredCount = requiredCourses.length;
                                const requiredMinutes = requiredCourses.reduce((sum, c) => sum + c.durationMinutes, 0);
                                return (
                                    <span style={{
                                        padding: "6px 16px",
                                        background: "#FEE2E2",
                                        borderRadius: "20px",
                                        fontSize: "13px",
                                        color: "#DC2626",
                                        fontWeight: 500,
                                        border: "1px solid #FECACA"
                                    }}>
                                        This month&apos;s required training: {requiredCount} courses - {formatDuration(requiredMinutes)}
                                    </span>
                                );
                            })()}
                        </div>

                        {/* Training Courses Accordion List */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {trainingCourses.filter(c => c.isActive).map((course) => {
                                const isExpanded = expandedTrainingId === course.id;
                                const isComingSoon = course.status === "Coming Soon";
                                const isOverdue = course.dueDate && new Date(course.dueDate) < new Date();
                                const lessons = course.lessonsJson ? JSON.parse(course.lessonsJson) : [];

                                return (
                                    <div
                                        key={course.id}
                                        style={{
                                            background: "#FFFFFF",
                                            border: `1px solid ${isExpanded ? "#D0E3F7" : "#E1E5EB"}`,
                                            borderRadius: "12px",
                                            overflow: "hidden",
                                            boxShadow: isExpanded ? "0 4px 16px rgba(0, 120, 212, 0.08)" : "none",
                                            transition: "all 0.2s ease"
                                        }}
                                    >
                                        {/* Course Header Row */}
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "16px 20px",
                                                gap: "16px",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => setExpandedTrainingId(isExpanded ? null : course.id)}
                                        >
                                            {/* Coming Soon Badge (if applicable) */}
                                            {isComingSoon && (
                                                <span style={{
                                                    padding: "6px 14px",
                                                    borderRadius: "6px",
                                                    fontSize: "12px",
                                                    fontWeight: 700,
                                                    textTransform: "uppercase",
                                                    background: "#FEF3C7",
                                                    color: "#A16207",
                                                    border: "1px solid #FDE68A",
                                                    flexShrink: 0
                                                }}>
                                                    COMING SOON!
                                                </span>
                                            )}

                                            {/* Required/Optional Badge - Larger size */}
                                            <span style={{
                                                padding: "6px 14px",
                                                borderRadius: "6px",
                                                fontSize: "12px",
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                background: course.isRequired ? "#FEE2E2" : "#E0F2FE",
                                                color: course.isRequired ? "#DC2626" : "#0369A1",
                                                border: course.isRequired ? "1px solid #FECACA" : "1px solid #BAE6FD",
                                                flexShrink: 0
                                            }}>
                                                {course.isRequired ? "REQUIRED" : "OPTIONAL"}
                                            </span>

                                            {/* Title */}
                                            <span style={{
                                                flex: 1,
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                color: isComingSoon ? "#9CA3AF" : "#1A1A2E"
                                            }}>
                                                {course.title}
                                            </span>

                                            {/* Duration */}
                                            <span style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                fontSize: "13px",
                                                color: "#6B7280",
                                                flexShrink: 0
                                            }}>
                                                ⏱ {formatDuration(course.durationMinutes)}
                                            </span>

                                            {/* Due Date */}
                                            {course.dueDate ? (
                                                <span style={{
                                                    padding: "6px 12px",
                                                    borderRadius: "6px",
                                                    fontSize: "12px",
                                                    fontWeight: 500,
                                                    background: isOverdue ? "#FEE2E2" : "#FFF7ED",
                                                    color: isOverdue ? "#DC2626" : "#C2410C",
                                                    border: isOverdue ? "1px solid #FECACA" : "1px solid #FED7AA",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    flexShrink: 0
                                                }}>
                                                    ⏰ Due {formatDate(course.dueDate)}
                                                </span>
                                            ) : (
                                                <span style={{
                                                    padding: "6px 12px",
                                                    borderRadius: "6px",
                                                    fontSize: "12px",
                                                    fontWeight: 500,
                                                    background: "#F3F4F6",
                                                    color: "#6B7280",
                                                    flexShrink: 0
                                                }}>
                                                    ⏰ Due TBD
                                                </span>
                                            )}

                                            {/* Action Button */}
                                            {isComingSoon ? (
                                                <button style={{
                                                    padding: "8px 16px",
                                                    borderRadius: "6px",
                                                    background: "#0078D4",
                                                    color: "#FFFFFF",
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                    border: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    flexShrink: 0
                                                }}>
                                                    Notify me →
                                                </button>
                                            ) : course.link ? (
                                                <a
                                                    href={course.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        padding: "8px 16px",
                                                        borderRadius: "6px",
                                                        background: "#0078D4",
                                                        color: "#FFFFFF",
                                                        fontSize: "13px",
                                                        fontWeight: 500,
                                                        textDecoration: "none",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    Go to training →
                                                </a>
                                            ) : null}

                                            {/* Expand/Collapse Arrow */}
                                            <span style={{
                                                fontSize: "18px",
                                                color: "#9CA3AF",
                                                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                                transition: "transform 0.2s ease",
                                                flexShrink: 0
                                            }}>
                                                ▼
                                            </span>
                                        </div>

                                        {/* Expanded Details */}
                                        {isExpanded && (
                                            <div style={{
                                                padding: "0 20px 20px 20px",
                                                display: "flex",
                                                gap: "40px",
                                                animation: "fadeIn 0.2s ease"
                                            }}>
                                                {/* Left: Description & Meta */}
                                                <div style={{ flex: 1 }}>
                                                    <p style={{
                                                        fontSize: "14px",
                                                        color: "#5A5A72",
                                                        lineHeight: 1.6,
                                                        margin: "0 0 16px 0"
                                                    }}>
                                                        {course.description}
                                                    </p>
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "20px",
                                                        fontSize: "13px",
                                                        color: "#6B7280"
                                                    }}>
                                                        {course.smeName && (
                                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                👤 SME <strong style={{ color: "#1A1A2E" }}>{course.smeName}</strong>
                                                            </span>
                                                        )}
                                                        {course.releaseDate && (
                                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                📅 Released {formatDate(course.releaseDate)} · {getTimeAgo(course.releaseDate)}
                                                            </span>
                                                        )}
                                                        {course.portal && (
                                                            <span style={{
                                                                padding: "3px 10px",
                                                                borderRadius: "4px",
                                                                background: "#DCFCE7",
                                                                color: "#16A34A",
                                                                fontWeight: 600,
                                                                fontSize: "12px"
                                                            }}>
                                                                {course.portal}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Right: Lessons List */}
                                                {lessons.length > 0 && (
                                                    <div style={{
                                                        minWidth: "280px",
                                                        background: "#F9FAFB",
                                                        borderRadius: "10px",
                                                        padding: "16px 20px"
                                                    }}>
                                                        <div style={{
                                                            fontSize: "11px",
                                                            fontWeight: 600,
                                                            textTransform: "uppercase",
                                                            color: "#9CA3AF",
                                                            marginBottom: "12px",
                                                            letterSpacing: "0.5px"
                                                        }}>
                                                            THIS COURSE CONTAINS
                                                        </div>
                                                        {lessons.map((lesson: any, idx: number) => (
                                                            <div
                                                                key={idx}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "space-between",
                                                                    padding: "8px 0",
                                                                    borderBottom: idx < lessons.length - 1 ? "1px solid #E5E7EB" : "none"
                                                                }}
                                                            >
                                                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                                    <span style={{
                                                                        fontSize: "12px",
                                                                        color: "#9CA3AF",
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {String(idx + 1).padStart(2, "0")}
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: "13px",
                                                                        color: "#374151"
                                                                    }}>
                                                                        {lesson.title}
                                                                    </span>
                                                                </div>
                                                                <span style={{
                                                                    fontSize: "12px",
                                                                    color: "#9CA3AF"
                                                                }}>
                                                                    {lesson.durationMinutes} min
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Floating Agent Button */}
            <div
                onClick={isFetchingToken ? undefined : handleOpenAgentPanel}
                style={{
                    position: "fixed",
                    bottom: "24px",
                    right: "24px",
                    width: "56px",
                    height: "56px",
                    borderRadius: "16px",
                    background: isFetchingToken
                        ? "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
                        : "linear-gradient(135deg, #0078d4 0%, #005a9e 100%)",
                    boxShadow: "0 4px 16px rgba(0, 120, 212, 0.3)",
                    cursor: isFetchingToken ? "wait" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                    transition: "all 0.2s ease",
                    border: "none"
                }}
                onMouseEnter={(e) => {
                    if (!isFetchingToken) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 120, 212, 0.4)";
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 120, 212, 0.3)";
                }}
                title={isFetchingToken ? "Connecting..." : "Open MCS AI Assistant"}
            >
                {isFetchingToken ? (
                    <div style={{
                        width: "24px",
                        height: "24px",
                        border: "3px solid rgba(255, 255, 255, 0.3)",
                        borderTopColor: "#ffffff",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                    }} />
                ) : (
                    <img src={CopilotLogo} alt="Copilot" style={{ width: "32px", height: "32px", filter: "brightness(10)" }} />
                )}
            </div>

            {/* Agent Panel - Custom sliding panel that never unmounts */}
            {/* Backdrop */}
            <div
                onClick={() => {
                    setIsAgentPanelOpen(false);
                    logEvent("MCS_Agent_PanelClosed", "MCS Report Catalog", "AgentPanel", { method: "backdrop" });
                }}
                style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0, 0, 0, 0.3)",
                    zIndex: 1000,
                    opacity: isAgentPanelOpen ? 1 : 0,
                    visibility: isAgentPanelOpen ? "visible" : "hidden",
                    transition: "opacity 0.3s ease, visibility 0.3s ease",
                    pointerEvents: isAgentPanelOpen ? "auto" : "none"
                }}
            />

            {/* Sliding Panel */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    right: 0,
                    width: "400px",
                    height: "100vh",
                    background: "#ffffff",
                    boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.08)",
                    zIndex: 1001,
                    display: "flex",
                    flexDirection: "column",
                    transform: isAgentPanelOpen ? "translateX(0)" : "translateX(100%)",
                    transition: "transform 0.3s ease",
                    fontFamily: "'Roboto', sans-serif"
                }}
            >
                {/* Clean Header */}
                <div style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            background: "#0078d4",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <img src={CopilotLogo} alt="Copilot" style={{ width: "24px", height: "24px", filter: "brightness(10)" }} />
                        </div>
                        <div>
                            <div style={{ fontSize: "15px", fontWeight: 600, color: "#1a1a2e" }}>
                                MCS Assistant
                            </div>
                            <div style={{
                                fontSize: "12px",
                                color: agentConnected ? "#16a34a" : agentLoading ? "#d97706" : "#6b7280",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px"
                            }}>
                                <span style={{
                                    width: "6px",
                                    height: "6px",
                                    borderRadius: "50%",
                                    background: agentConnected ? "#22c55e" : agentLoading ? "#f59e0b" : "#9ca3af"
                                }} />
                                {agentConnected ? "Ready" : agentLoading ? "Connecting..." : "Offline"}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setIsAgentPanelOpen(false);
                            logEvent("MCS_Agent_PanelClosed", "MCS Report Catalog", "AgentPanel", { method: "closeButton" });
                        }}
                        style={{
                            background: "transparent",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "background 0.2s"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                        <Dismiss24Regular style={{ color: "#6b7280", fontSize: "16px" }} />
                    </button>
                </div>

                {/* Error Banner */}
                {agentError && (
                    <div style={{ padding: "12px 16px", background: "#fef2f2", borderBottom: "1px solid #fecaca" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Warning20Regular style={{ color: "#dc2626", flexShrink: 0 }} />
                            <span style={{ fontSize: "13px", color: "#991b1b", flex: 1 }}>{agentError}</span>
                            <button
                                onClick={async () => {
                                    setAgentError(null);
                                    setAgentConnected(false);
                                    webChatInitialized.current = false;
                                    // If no token, re-fetch it first
                                    if (!directLineToken) {
                                        try {
                                            setIsFetchingToken(true);
                                            const tokenResult = await getDirectLineToken();
                                            if (tokenResult.data?.token) {
                                                setDirectLineToken(tokenResult.data.token);
                                            }
                                        } catch {
                                            // initializeWebChat will handle the error
                                        } finally {
                                            setIsFetchingToken(false);
                                        }
                                    } else {
                                        initializeWebChat();
                                    }
                                }}
                                style={{
                                    background: "#dc2626",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "5px 10px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    fontWeight: 500
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                )}

                {/* WebChat Container */}
                <div style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    background: "#ffffff",
                    position: "relative",
                    overflowY: "auto"
                }}>
                    {agentLoading && (
                        <div style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "20px",
                            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                            zIndex: 10
                        }}>
                            {/* Animated Copilot Icon */}
                            <div style={{
                                position: "relative",
                                width: "80px",
                                height: "80px"
                            }}>
                                <div style={{
                                    position: "absolute",
                                    inset: 0,
                                    borderRadius: "50%",
                                    border: "3px solid #e5e7eb",
                                    borderTopColor: "#0078d4",
                                    animation: "spin 1s linear infinite"
                                }} />
                                <div style={{
                                    position: "absolute",
                                    inset: "8px",
                                    borderRadius: "50%",
                                    background: "linear-gradient(135deg, #0078d4 0%, #005a9e 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 4px 16px rgba(0, 120, 212, 0.3)"
                                }}>
                                    <img src={CopilotLogo} alt="" style={{ width: "36px", height: "36px", filter: "brightness(10)" }} />
                                </div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a2e", marginBottom: "6px" }}>
                                    Connecting to MCS Assistant
                                </div>
                                <div style={{ fontSize: "13px", color: "#6b7280" }}>
                                    Initializing secure connection...
                                </div>
                            </div>
                            {/* Animated dots */}
                            <div style={{ display: "flex", gap: "6px" }}>
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#0078d4", animation: "bounce 1.4s infinite ease-in-out", animationDelay: "0s" }} />
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#0078d4", animation: "bounce 1.4s infinite ease-in-out", animationDelay: "0.2s" }} />
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#0078d4", animation: "bounce 1.4s infinite ease-in-out", animationDelay: "0.4s" }} />
                            </div>
                        </div>
                    )}
                    <div
                        ref={webChatRef}
                        style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            visibility: agentLoading ? "hidden" : "visible"
                        }}
                    />
                    {/* Custom waiting indicator - shows while agent is generating response */}
                    {isWaitingResponse && !agentLoading && (
                        <div
                            className="thinking-container"
                            style={{
                                position: "absolute",
                                bottom: "70px",
                                left: "16px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "linear-gradient(135deg, #0078d4 0%, #005a9e 100%)",
                                borderRadius: "16px",
                                padding: "10px 18px",
                                zIndex: 5
                            }}>
                            <div className="thinking-dot" style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ffffff" }} />
                            <div className="thinking-dot" style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ffffff" }} />
                            <div className="thinking-dot" style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ffffff" }} />
                        </div>
                    )}
                </div>

                {/* WebChat Styles */}
                <style>{`
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes bounce {
                        0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
                        40% { transform: scale(1); opacity: 1; }
                    }
                    @keyframes thinking-wave {
                        0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
                        30% { transform: translateY(-6px); opacity: 1; }
                    }
                    @keyframes typing-glow {
                        0%, 100% { box-shadow: 0 4px 12px rgba(0, 120, 212, 0.25); }
                        50% { box-shadow: 0 4px 20px rgba(0, 120, 212, 0.5); }
                    }
                    
                    /* Custom thinking dots animation */
                    .thinking-container {
                        animation: typing-glow 2s infinite ease-in-out;
                    }
                    .thinking-dot {
                        animation: thinking-wave 1.4s infinite ease-in-out;
                    }
                    .thinking-dot:nth-child(1) { animation-delay: 0s; }
                    .thinking-dot:nth-child(2) { animation-delay: 0.2s; }
                    .thinking-dot:nth-child(3) { animation-delay: 0.4s; }
                    
                    /* WebChat font override */
                    [class^="webchat"] {
                        font-family: 'Roboto', sans-serif !important;
                    }
                    
                    /* Hide default WebChat typing indicator - we use custom one */
                    .webchat__typing-indicator {
                        display: none !important;
                    }
                    
                    /* Fix send box to prevent layout shifts */
                    .webchat__send-box {
                        flex-shrink: 0 !important;
                        min-height: 50px !important;
                    }
                    
                    /* Ensure chat container is stable */
                    .webchat__basic-transcript {
                        flex: 1 1 auto !important;
                        overflow-y: auto !important;
                    }
                `}</style>
            </div>
        </div >
    );
};

export default MCSReportCatalog;
